import { Group, Progress, Table, Text } from "@mantine/core";
import { IncomeTaxBreakdown } from "backend/src/types";
import React from "react";
import { gbpFormatter, getTitle, percFormatter } from "../../utils";
import classes from "./TableSalaries.module.css";

type TableSalariesProps = {
  data: IncomeTaxBreakdown[];
};

export const TableSalaries: React.FC<TableSalariesProps> = ({ data }) => {
  const annualDataKeys = [
    "title",
    "grossIncome",
    // "taxableIncome",
    "incomeTax",
    "nationalInsuranceTax",
    // "tax",
    "net",
    "studentLoanDeductions",
    "pensionDeductions",
    // "deductions",
    "takeHome",
    "incomeDistribution",
  ];

  const rowHeaders = [
    "",
    "Gross Income",
    // "Taxable Income",
    "Income Tax",
    "National Insurance",
    // "Total Tax",
    "Net Income",
    "Student Loan",
    "Pension Deductions",
    // "Total Deductions",
    "Take Home",
    "Income Distribution",
  ];

  const renderColumn = (salary: IncomeTaxBreakdown, key: string) => {
    switch (key) {
      case "title":
        return <Table.Th>Annually</Table.Th>;
      case "titleMonthly":
        return <Table.Th>Monthly</Table.Th>;
      case "incomeDistribution":
        return (
          <Table.Td colSpan={2}>
            <Group justify="space-between">
              <Text fz="xs" c="teal" fw={700}>
                {percFormatter(
                  salary.result.takeHome / salary.result.grossIncome
                )}{" "}
                take home
              </Text>
              <Text fz="xs" c="yellow" fw={700}>
                {percFormatter(
                  salary.result.deductions / salary.result.grossIncome
                )}{" "}
                deductions
              </Text>
              <Text fz="xs" c="red" fw={700}>
                {percFormatter(salary.result.tax / salary.result.grossIncome)}{" "}
                tax
              </Text>
            </Group>
            <Progress.Root>
              <Progress.Section
                className={classes.progressSection}
                value={
                  (100 * salary.result.takeHome) / salary.result.grossIncome
                }
                color="teal"
              />
              <Progress.Section
                className={classes.progressSection}
                value={
                  (100 * salary.result.deductions) / salary.result.grossIncome
                }
                color="yellow"
              />
              <Progress.Section
                className={classes.progressSection}
                value={(100 * salary.result.tax) / salary.result.grossIncome}
                color="red"
              />
            </Progress.Root>
          </Table.Td>
        );
      case "studentLoanDeductions":
        return (
          <Table.Td>
            {gbpFormatter(
              salary.result.studentLoanDeductions.reduce(
                (total, deduction) => total + deduction.annualDeduction,
                0
              ) / 100.0
            )}
          </Table.Td>
        );
      case "studentLoanDeductionsMonthly":
        return (
          <Table.Td>
            {gbpFormatter(
              salary.result.studentLoanDeductions.reduce(
                (total, deduction) => total + deduction.monthlyDeduction,
                0
              ) / 100.0
            )}
          </Table.Td>
        );
      case "grossIncome":
      case "taxableIncome":
      case "incomeTax":
      case "nationalInsuranceTax":
      case "tax":
      case "net":
      case "pensionDeductions":
      case "deductions":
      case "takeHome":
        return <Table.Td>{gbpFormatter(salary.result[key] / 100.0)}</Table.Td>;
      case "grossIncomeMonthly":
      case "taxableIncomeMonthly":
      case "incomeTaxMonthly":
      case "nationalInsuranceTaxMonthly":
      case "taxMonthly":
      case "netMonthly":
      case "pensionDeductionsMonthly":
      case "deductionsMonthly":
      case "takeHomeMonthly":
        return <Table.Td>{gbpFormatter(salary.result[key] / 100.0)}</Table.Td>;
      default:
        return null;
    }
  };

  const renderRows = React.useMemo(
    () =>
      annualDataKeys.map((key, idx) => (
        <Table.Tr key={key}>
          <Table.Th className={classes.header}>{rowHeaders[idx]}</Table.Th>
          {data.length > 0 && renderColumn(data[0], key)}
          {data.length > 0 && renderColumn(data[0], `${key}Monthly`)}
          {data.length > 1 && renderColumn(data[1], key)}
          {data.length > 1 && renderColumn(data[1], `${key}Monthly`)}
        </Table.Tr>
      )),
    [data]
  );

  return (
    <Table.ScrollContainer mt="md" minWidth={190 + 290 * data.length}>
      <Table
        striped
        withTableBorder
        withColumnBorders
        highlightOnHover
        verticalSpacing="xs"
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th className={classes.header} />
            {data.map((salary, idx) => (
              <Table.Th key={`th-salary-${idx}`} colSpan={2}>
                <Group align="flex-end" gap="xs" mt={25}>
                  Salary {idx + 1}
                </Group>
                <Text fz="xs">{getTitle(salary.inputs)}</Text>{" "}
                {/* <Anchor component="button" fz="sm">
                  Share
                </Anchor> */}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{renderRows}</Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};
